import { Box, Typography, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import React from "react";
import { useHistory } from "react-router";

import { CustomButton } from "@/components/Button";
import { LoadingPanel, FullPageError } from "@/components/StatePanels";
import { fontFamilies } from "@/designTokens";
import { useFromHomeEntry } from "@/hooks/useFromHomeEntry";
import { DiagnosisLayout, diagnosisActionStyles } from "@/layouts/DiagnosisLayout";
import theme from "@/theme";
import {
  ANXIETY_RESULT_LABEL,
  ASSET_LABEL,
  HOLDING_RESULT_LABEL,
  INTEREST_LABEL,
  MEETING_QUESTION_TEXT,
  MONTHLY_RESULT_LABEL,
  SUCCESS_CONDITION_LABEL,
  WARNING_LABEL,
  buildHeadline,
  buildStance,
  buildTypeDisplay,
} from "@/utilities/resultLabels";

import HeroImage from "@assets/image/diagnosis_result_hero.png";

import { DiagnosisInvestmentStyleResultError } from "./components/DiagnosisInvestmentStyleResultError";
import { ResultReveal } from "./components/ResultReveal";
import { useGetInvestmentStyleDiagnosis } from "./hooks/useGetInvestmentStyleDiagnosis";
import { buildResultExit } from "./utils/resultExit";

const useStyles = makeStyles(() => ({
  "@keyframes coverEnter": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes coverLight": {
    from: { opacity: 0, transform: "translateX(-100%)" },
    "30%": { opacity: 1 },
    to: { opacity: 0, transform: "translateX(100%)" },
  },
  "@keyframes titleLine": {
    from: { transform: "scaleX(0)" },
    to: { transform: "scaleX(1)" },
  },
  loadingRoot: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  hero: {
    position: "relative",
    isolation: "isolate",
    overflow: "hidden",
    padding: "32px 24px 24px",
    textAlign: "center",
    color: theme.palette.common.white,
    backgroundImage: `url(${HeroImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundColor: theme.palette.semantic["surface/trust"],
    [theme.breakpoints.up("md")]: { padding: "48px 32px 32px" },
    "&::before": {
      content: "\"\"",
      position: "absolute",
      inset: 0,
      zIndex: -1,
      pointerEvents: "none",
      background: "linear-gradient(115deg, transparent 25%, rgba(255,255,255,0.16) 48%, transparent 70%)",
      animation: "$coverLight 1800ms ease-in-out 100ms both",
    },
    "@media (prefers-reduced-motion: reduce), print": {
      "&&&& *, &&&& *::after": { animation: "none" },
      "&&::before": { animation: "none", opacity: 0 },
    },
  },
  pageTitle: {
    animation: "$coverEnter 720ms cubic-bezier(0.22, 1, 0.36, 1) 60ms both",
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.3,
    color: "inherit",
    [theme.breakpoints.up("md")]: { fontSize: 32 },
    "&:after": {
      content: "\"\"",
      display: "block",
      width: "100%",
      height: 1,
      backgroundColor: theme.palette.common.white,
      opacity: 0.8,
      margin: "16px auto 24px",
      animation: "$titleLine 700ms cubic-bezier(0.22, 1, 0.36, 1) 240ms both",
    },
  },
  headerSummary: {
    animation: "$coverEnter 700ms cubic-bezier(0.22, 1, 0.36, 1) 220ms both",
    padding: 16,
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.secondary,
    textAlign: "left",
    [theme.breakpoints.up("md")]: { padding: 24, borderRadius: 24 },
  },
  headerTitle: {
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTypeText: { flex: 1, minWidth: 0 },
  headerTypeLabel: { fontSize: 14, color: "inherit", lineHeight: 1.5, [theme.breakpoints.up("md")]: { fontSize: 16 } },
  headerType: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.primary.main,
    marginTop: 4,
    [theme.breakpoints.up("md")]: { fontSize: 28 },
  },
  headerImage: {
    width: 64,
    height: 64,
    objectFit: "contain",
    flexShrink: 0,
    [theme.breakpoints.up("md")]: { width: 80, height: 80 },
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: {
    padding: "4px 12px",
    borderRadius: 9999,
    backgroundColor: theme.palette.semantic["state/selected-bg"],
    color: theme.palette.text.primary,
    fontSize: 12,
    lineHeight: 1.5,
  },
  headerStance: {
    fontSize: 14,
    lineHeight: 1.5,
    color: "inherit",
    marginTop: 16,
    "&:first-child": { marginTop: 0 },
    [theme.breakpoints.up("md")]: { fontSize: 18 },
  },
  // Layout の末尾64pxと合わせて、固定ボタンの高さ＋safe area分の余白を確保する。
  content: { padding: "24px 24px calc(32px + env(safe-area-inset-bottom, 0px))" },
  section: {
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    boxShadow: "0 4px 12px rgba(0,16,155,.1)",
    padding: theme.spacing(2.5),
    marginBottom: 16,
  },
  // 番号見出しの下には線を引かない。線はカードごとに区切る位置へ divider で置く（Figma の Divider の位置）
  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // カード内のまとまりの区切り線。上下 16 は前後の要素の余白と重なり、線の両側とも 16 になる
  divider: {
    height: 1,
    backgroundColor: theme.palette.semantic["border/default"],
    margin: theme.spacing(2, 0),
  },
  sectionBadge: {
    width: 22,
    height: 22,
    borderRadius: 9999,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 12,
    fontFamily: fontFamilies.number,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  // ①の大見出し。番号見出しとひとまとまり（間 8）にし、区切り線はこの後に置く
  headline: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
  },
  blockLabel: {
    fontSize: 14,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
    fontWeight: 700,
    lineHeight: 1.5,
    color: theme.palette.semantic["text/heading"],
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  barLabel: {
    width: 126,
    flexShrink: 0,
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
    fontWeight: 500,
  },
  barTrack: {
    width: "100%",
    height: 8,
    borderRadius: 999,
    backgroundColor: theme.palette.semantic["surface/base"],
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: theme.palette.primary.main,
  },
  // ②は区切り線なしで、番号見出しの 16 下に箱を並べる
  stanceBoxes: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1.5),
    // スマホ幅では2列で表示する
    [theme.breakpoints.down("sm")]: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  },
  stanceBox: {
    flex: 1,
    minWidth: 0,
    border: "none",
    backgroundColor: theme.palette.semantic["surface/base"],
    borderRadius: 12,
    padding: "16px 12px",
  },
  stanceBoxLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  stanceBoxValue: {
    fontSize: 14,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  assetItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    fontSize: 14,
    fontWeight: 400,
    padding: 0,
    marginBottom: 8,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
    lineHeight: 1.5,
  },
  assetMark: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    marginTop: 9,
    flexShrink: 0,
  },
  assetNote: {
    fontSize: 12,
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
  avoidBox: {
    border: "none",
    backgroundColor: theme.palette.semantic["status/info-bg"],
    borderRadius: 8,
    padding: theme.spacing(1.5),
  },
  avoidLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  avoidText: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    padding: theme.spacing(0.25, 0),
  },
  floatingActions: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.appBar,
    padding: "24px 24px calc(16px + env(safe-area-inset-bottom, 0px))",
    background: `linear-gradient(180deg, ${alpha(theme.palette.semantic["surface/base"], 0)} 0%, ${alpha(theme.palette.semantic["surface/base"], 0.98)} 45%)`,
    pointerEvents: "none",
    [theme.breakpoints.up("md")]: {
      width: 720,
      left: "50%",
      right: "auto",
      transform: "translateX(-50%)",
    },
    "@media print": { position: "static", width: "100%", transform: "none", background: "none" },
  },
  buttonWrap: {
    ...diagnosisActionStyles,
    margin: "0 auto",
    pointerEvents: "auto",
    borderRadius: 999,
    boxShadow: `0 8px 24px ${alpha(theme.palette.semantic["surface/trust"], 0.22)}`,
  },
}));

export const DiagnosisInvestmentStyleResultPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { diagnosis, loading, error, sessionExpired, retry } = useGetInvestmentStyleDiagnosis();
  // 入口の判定は履歴エントリの state から。エントリが生きている限り値は変わらないため凍結は不要
  const fromHome = useFromHomeEntry();

  const exit = buildResultExit(fromHome);

  const handleNext = React.useCallback(() => {
    if (fromHome) {
      // push だとホーム到達後に「戻る」でこの画面へ復帰してしまうため、履歴に残さないよう replace で遷移する
      history.replace(exit.path);
      return;
    }
    history.push(exit.path);
  }, [exit.path, fromHome, history]);

  if (loading) {
    return (
      <Box className={classes.loadingRoot}>
        <LoadingPanel message="" />
      </Box>
    );
  }

  if (sessionExpired) {
    // 再試行では復帰しないため、送信側（useSubmitInvestmentStyleDiagnosis）と同じ案内文言に揃える。
    return (
      <Box className={classes.loadingRoot}>
        <FullPageError description="結果の取得に失敗しました。画面をリロードし、再度ログインしてください。" />
      </Box>
    );
  }

  if (error || !diagnosis) {
    return (
      <Box className={classes.loadingRoot}>
        <DiagnosisInvestmentStyleResultError onRetry={retry} />
      </Box>
    );
  }

  // 古いレコードに中心目的がない場合、「入口は節税、本質は」で見出しを途切れさせない。
  const headline = buildHeadline(diagnosis);
  const stance = buildStance(diagnosis);
  // タイプ名・画像の判断はホームのサマリーカードと共通（buildTypeDisplay）。
  // customer_type nil / 辞書に無いタイプでは「タイプ判定なし」とは書かず、タイプの行ごと出さない
  const typeDisplay = buildTypeDisplay(diagnosis);
  const interestScores = diagnosis.interest_scores ?? [];
  const anxietyScores = diagnosis.anxiety_scores ?? [];
  const suitedAssets = diagnosis.suited_assets ?? [];
  const primaryAssets = suitedAssets.slice(0, Math.min(3, suitedAssets.length));
  const otherAssets = suitedAssets.slice(Math.min(3, suitedAssets.length));
  const warnings = diagnosis.avoid_sales_style_warnings ?? [];
  const meetingQuestions = diagnosis.meeting_questions ?? [];
  const successConditions = diagnosis.success_conditions ?? [];

  return (
    <DiagnosisLayout mode="result">
      <Box className={classes.hero}>
        <Typography component="h1" className={classes.pageTitle}>
          あなたの投資スタイル
        </Typography>
        {(typeDisplay.typed || stance) && (
          <Box className={classes.headerSummary} data-testid="style-result-summary">
            {typeDisplay.typed && (
              <Box className={classes.headerTitle} data-testid="style-result-type">
                <Box className={classes.headerTypeText}>
                  <Typography className={classes.headerTypeLabel}>{typeDisplay.label}</Typography>
                  <Typography className={classes.headerType}>{typeDisplay.headline}</Typography>
                </Box>
                <img className={classes.headerImage} src={typeDisplay.character} alt={typeDisplay.headline} />
              </Box>
            )}
            {stance && <Typography className={classes.headerStance}>{stance}</Typography>}
          </Box>
        )}
      </Box>
      <Box className={classes.content}>
      {/* ① 目的と理想 */}
      <ResultReveal className={classes.section}>
        <Box className={classes.sectionHead}>
          <span className={classes.sectionBadge}>1</span>
          <Typography className={classes.sectionTitle}>目的と理想</Typography>
        </Box>
        {headline && <Typography className={classes.headline}>{headline}</Typography>}
        <Typography className={classes.blockLabel}>関心の強さ</Typography>
        {interestScores.map((item) => (
          <Box key={item.key} className={classes.barRow}>
            <Typography className={classes.barLabel}>{item.key ? INTEREST_LABEL[item.key] : ""}</Typography>
            <Box className={classes.barTrack}>
              <Box className={classes.barFill} data-result-bar style={{ width: `${item.score ?? 0}%` }} />
            </Box>
          </Box>
        ))}
        {successConditions.length > 0 && (
          <>
            <Typography className={classes.blockLabel}>成功と感じる条件</Typography>
            <Box className={classes.chips}>
              {successConditions.map((key) => (
                <span key={key} className={classes.chip}>
                  {SUCCESS_CONDITION_LABEL[key]}
                </span>
              ))}
            </Box>
          </>
        )}
        {diagnosis.ideal_text && (
          <>
            <Typography className={classes.blockLabel}>実現したい未来</Typography>
            <Typography>{diagnosis.ideal_text}</Typography>
          </>
        )}
      </ResultReveal>

      {/* ② 投資スタンス */}
      <ResultReveal className={classes.section}>
        <Box className={classes.sectionHead}>
          <span className={classes.sectionBadge}>2</span>
          <Typography className={classes.sectionTitle}>投資スタンス</Typography>
        </Box>
        <Box className={classes.stanceBoxes}>
          <Box className={classes.stanceBox}>
            <Typography className={classes.stanceBoxLabel}>保有期間</Typography>
            <Typography className={classes.stanceBoxValue}>
              {diagnosis.holding_period ? HOLDING_RESULT_LABEL[diagnosis.holding_period] : "—"}
            </Typography>
          </Box>
          <Box className={classes.stanceBox}>
            <Typography className={classes.stanceBoxLabel}>毎月の持ち出し</Typography>
            <Typography className={classes.stanceBoxValue}>
              {diagnosis.monthly_out_of_pocket ? MONTHLY_RESULT_LABEL[diagnosis.monthly_out_of_pocket] : "—"}
            </Typography>
          </Box>
        </Box>
      </ResultReveal>

      {/* ③ 検討すべき物件タイプ */}
      <ResultReveal className={classes.section}>
        <Box className={classes.sectionHead}>
          <span className={classes.sectionBadge}>3</span>
          <Typography className={classes.sectionTitle}>検討すべき物件タイプ</Typography>
        </Box>
        <Typography className={classes.blockLabel}>向いているアセット</Typography>
        {primaryAssets.map((asset) => (
          <Box key={asset} className={classes.assetItem}>
            <span className={classes.assetMark} />
            {ASSET_LABEL[asset]}
          </Box>
        ))}
        {otherAssets.length > 0 && (
          <>
            <Typography className={classes.blockLabel}>その他の候補</Typography>
            {otherAssets.map((asset) => (
              <Box key={asset} className={classes.assetItem}>
                <span className={classes.assetMark} />
                {ASSET_LABEL[asset]}
              </Box>
            ))}
          </>
        )}
        <Typography className={classes.assetNote}>※「向いているもの」だけを前向きに提示しています。</Typography>
      </ResultReveal>

      {/* ④ リスク許容度 */}
      <ResultReveal className={classes.section}>
        <Box className={classes.sectionHead}>
          <span className={classes.sectionBadge}>4</span>
          <Typography className={classes.sectionTitle}>リスク許容度</Typography>
        </Box>
        <Typography className={classes.blockLabel}>不安に感じていること</Typography>
        {anxietyScores.map((item) => (
          <Box key={item.key} className={classes.barRow}>
            <Typography className={classes.barLabel}>{item.key ? ANXIETY_RESULT_LABEL[item.key] : ""}</Typography>
            <Box className={classes.barTrack}>
              <Box className={classes.barFill} data-result-bar style={{ width: `${item.score ?? 0}%` }} />
            </Box>
          </Box>
        ))}
        {warnings.length > 0 && (
          <Box className={classes.avoidBox}>
            <Typography className={classes.avoidLabel}>避けるべき進め方：</Typography>
            {warnings.map((key) => (
              <Typography key={key} className={classes.avoidText}>
                {WARNING_LABEL[key]}
              </Typography>
            ))}
          </Box>
        )}
      </ResultReveal>

      {/* ⑤ 面談で聞くと良い質問 */}
      <ResultReveal className={classes.section}>
        <Box className={classes.sectionHead}>
          <span className={classes.sectionBadge}>5</span>
          <Typography className={classes.sectionTitle}>面談で聞くと良い{meetingQuestions.length}つの質問</Typography>
        </Box>
        {meetingQuestions.map((question, index) => (
          <Box key={`${question.key}-${index}`} className={classes.assetItem}>
            <span className={classes.assetMark} />
            <span>{question.key && Object.prototype.hasOwnProperty.call(MEETING_QUESTION_TEXT, question.key) ? MEETING_QUESTION_TEXT[question.key] : "質問データを表示できません"}</span>
          </Box>
        ))}
      </ResultReveal>

      </Box>
      <Box className={classes.floatingActions} data-testid="result-floating-actions">
        <Box className={classes.buttonWrap}>
          <CustomButton type="button" tone="primary" buttonSize="L" fullWidth onClick={handleNext}>
            {exit.label}
          </CustomButton>
        </Box>
      </Box>
    </DiagnosisLayout>
  );
};
